import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useRef } from 'react'

import {
  usersAtom,
  fetchAllUsersLoadingAtom,
  fetchAllUsersErrorAtom,
  instructorsAtom,
  fetchAllInstructorsLoadingAtom,
  fetchAllInstructorsErrorAtom,
  fetchAllUsersFinishedAtom,
  fetchAllInstructorsFinishedAtom,
} from '../../domains/user/AtomUsers'
import { userUpsertManyCommandAtom, userUpsertOneCommandAtom, userResetAllCommandAtom } from '../../domains/user/UserCommand'
import UserService from '../../services/UserService'
import useBar from '../common/Bar'

/**
 * 用户管理 Hook
 * 提供用户数据的获取、更新、缓存等功能
 * @param {Function} api - API 实例函数
 * @returns {Object} 用户管理相关的方法和状态
 */
export function useUserManagement() {
  const bar = useBar()

  const [fetchInstructorLoading, setFetchInstructorLoading] = useAtom(fetchAllInstructorsLoadingAtom)
  const [, setFetchInstructorError] = useAtom(fetchAllInstructorsErrorAtom)

  const [fetchAllInstructorsFinished, setFetchAllInstructorsFinished] = useAtom(fetchAllInstructorsFinishedAtom)
  const [fetchAllUsersFinished, setFetchAllUsersFinished] = useAtom(fetchAllUsersFinishedAtom)

  const [fetchAllUsersLoading, setFetchAllUsersLoading] = useAtom(fetchAllUsersLoadingAtom)
  const [, setFetchAllUsersError] = useAtom(fetchAllUsersErrorAtom)

  const [users] = useAtom(usersAtom)
  // use command to update a list of user
  const upsertUsers = useSetAtom(userUpsertManyCommandAtom)
  const upsertUser = useSetAtom(userUpsertOneCommandAtom)
  const resetAll = useSetAtom(userResetAllCommandAtom)

  const instructors = useAtomValue(instructorsAtom)

  // 使用 useRef 存储进行中的请求
  const pendingRequests = useRef({})
  // 使用 useRef 存储用户数据的最后更新时间
  const lastUpdateTime = useRef({})
  // 缓存过期时间（5分钟）
  const CACHE_EXPIRATION = 5 * 60 * 1000

  // 检查缓存是否有效
  const isCacheValid = useCallback((userId) => {
    const lastUpdate = lastUpdateTime.current[userId]
    return lastUpdate && Date.now() - lastUpdate < CACHE_EXPIRATION
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // 获取单个用户信息
  const fetchUser = useCallback(
    async ({ userId, forceRefresh = false }) => {
      // 如果缓存有效且不强制刷新，直接返回缓存数据
      if (!forceRefresh && users[userId] && isCacheValid(userId)) {
        return users[userId]
      }

      // 如果已经有相同的请求在进行中，返回该请求的 Promise
      if (pendingRequests.current[userId]) {
        return pendingRequests.current[userId]
      }

      // 创建新的请求
      const request = (async () => {
        try {
          const userData = await UserService.fetchUser(userId)
          upsertUser(userData)

          lastUpdateTime.current[userId] = Date.now()
          return userData
        } catch (error) {
          console.error('Failed to fetch user:', error)
          throw error
        } finally {
          // 请求完成后，删除进行中的请求记录
          delete pendingRequests.current[userId]
        }
      })()

      // 存储进行中的请求
      pendingRequests.current[userId] = request
      return request
    },
    [upsertUser, isCacheValid, users],
  )

  // 更新用户信息
  const updateUser = useCallback(async ({ userId, userData }) => {
    await UserService.updateUser(userId, userData)
    // 注意：这里不更新本地状态，因为服务器返回的数据可能不同
    // 如果需要，可以调用 fetchUser 来刷新数据
  }, [])

  // 批量获取用户信息
  const fetchUsers = useCallback(
    async ({ userIds = [], forceRefresh = false } = {}) => {
      const idsToFetch = userIds.filter((id) => forceRefresh || !users[id] || !isCacheValid(id))

      if (idsToFetch.length === 0) {
        return userIds.map((id) => users[id])
      }

      const filter = { userIds: Array.from(idsToFetch) }

      try {
        const usersData = await UserService.fetchUsers(filter)

        if (usersData.length > 0) {
          upsertUsers(usersData)
        }

        const newUsers = { ...users }
        usersData.forEach((user) => {
          newUsers[user.resourceId] = user
          lastUpdateTime.current[user.resourceId] = Date.now()
        })

        // 返回所有请求的用户数据
        return userIds.map((id) => newUsers[id])
      } catch (error) {
        console.error('Failed to fetch users:', error)
        throw error
      }
    },
    [users, isCacheValid, upsertUsers],
  )

  // 获取所有用户
  const fetchAllUsers = useCallback(
    async ({ forceRefresh = false } = {}) => {
      if (fetchAllUsersFinished && !forceRefresh) {
        return
      }

      const filter = { size: -1 }

      try {
        const usersData = await UserService.fetchUsers(filter)

        if (usersData.length > 0) {
          upsertUsers(usersData)
          setFetchAllUsersFinished(true)
        }

        usersData.forEach((user) => {
          lastUpdateTime.current[user.resourceId] = Date.now()
        })

        return usersData
      } catch (error) {
        console.error('Failed to fetch users:', error)
        throw error
      }
    },
    [fetchAllUsersFinished, upsertUsers, setFetchAllUsersFinished],
  )

  // 获取教练列表
  const fetchInstructors = useCallback(
    async (forceRefresh = false) => {
      // 如果已经在加载中，直接返回
      if (fetchInstructorLoading) {
        return instructors
      }

      // 如果有缓存且不强制刷新，直接返回缓存
      if (!forceRefresh && fetchAllInstructorsFinished && isCacheValid('instructors')) {
        return instructors
      }

      setFetchInstructorLoading(true)
      setFetchInstructorError(null)

      let fetchedInstructors = []
      const filter = { size: -1 }
      try {
        fetchedInstructors = await UserService.fetchInstructors(filter)
      } catch (error) {
        const message = error.message || 'Failed to fetch instructors'
        setFetchInstructorError(message)
        bar.error(message)
        console.error('Failed to fetch instructors:', error)
        throw error
      } finally {
        setFetchInstructorLoading(false)
        setFetchAllInstructorsFinished(true)
      }

      upsertUsers(fetchedInstructors)

      lastUpdateTime.current['instructors'] = Date.now()
      return fetchedInstructors
    },
    [
      setFetchInstructorLoading,
      setFetchInstructorError,
      setFetchAllInstructorsFinished,
      upsertUsers,
      instructors,
      isCacheValid,
      bar,
      fetchAllInstructorsFinished,
      fetchInstructorLoading,
    ],
  )

  // 初始化用户列表
  const initUsers = useCallback(async () => {
    if (fetchAllUsersFinished || fetchAllUsersLoading) {
      return
    }
    setFetchAllUsersLoading(true)
    await fetchAllUsers()
    setFetchAllUsersLoading(false)
  }, [fetchAllUsersFinished, fetchAllUsersLoading, fetchAllUsers, setFetchAllUsersLoading])

  // 初始化教练列表
  const initInstructors = useCallback(async () => {
    if (fetchAllInstructorsFinished && isCacheValid('instructors')) {
      return
    }
    await fetchInstructors()
  }, [fetchAllInstructorsFinished, isCacheValid, fetchInstructors])

  // 重置所有状态
  const reset = useCallback(() => {
    // 重置所有状态到初始值
    setFetchInstructorLoading(false)
    setFetchInstructorError(null)
    setFetchAllUsersLoading(false)
    setFetchAllUsersError(null)

    // use a custom setter to reset user list
    resetAll()

    // 清空请求缓存
    pendingRequests.current = {}
    // 清空时间缓存
    lastUpdateTime.current = {}
  }, [setFetchInstructorLoading, setFetchInstructorError, setFetchAllUsersLoading, setFetchAllUsersError, resetAll])

  return {
    // 方法
    updateUser,
    fetchUser,
    fetchUsers,
    fetchAllUsers,
    fetchInstructors,
    initUsers,
    initInstructors,
    reset,
  }
}
